import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, ArrowRight, Check, Eye, EyeOff } from 'lucide-react';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

export default function SignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', full_name: '', company_name: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error('Tu dois accepter les CGU et la politique de confidentialité');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Le mot de passe doit faire au moins 8 caractères');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/signup`, form, { withCredentials: true });
      toast.success(res.data.message || 'Inscription réussie !');
      // Petit délai pour montrer le toast puis redirection vers le dashboard
      setTimeout(() => {
        window.location.href = '/app';
      }, 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col">
      {/* Topbar */}
      <header className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-zinc-950" />
            </div>
            <span className="font-bold text-lg">Home <span className="text-amber-400">MDB 2A</span></span>
          </Link>
          <Link to="/login" className="text-sm text-zinc-400 hover:text-white">Déjà inscrit ? Se connecter</Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left : value props */}
          <div className="hidden lg:block">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold mb-6">
              ✨ Tarif fondateur 39€/mois
            </div>
            <h1 className="text-4xl font-bold leading-tight mb-6">
              Lance ton essai <span className="text-amber-400">14 jours</span>.<br />
              Sans CB. Sans engagement.
            </h1>
            <ul className="space-y-3 text-zinc-300">
              {[
                "Toutes les fonctionnalités débloquées",
                "Aucune carte demandée pour démarrer",
                "Tes données restent à toi (export RGPD à tout moment)",
                "Annulable en 1 clic depuis Stripe",
                "Support email direct avec Axel",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-amber-400" />
                  </div>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Right : form */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 backdrop-blur-sm">
            <h2 className="text-2xl font-bold mb-2">Créer mon compte</h2>
            <p className="text-zinc-400 text-sm mb-6">14 jours gratuits, sans CB.</p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-1.5">Nom complet *</label>
                <input
                  required
                  name="full_name"
                  value={form.full_name}
                  onChange={onChange}
                  placeholder="Axel AGUANNO"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                  data-testid="signup-full-name"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-1.5">Société (optionnel)</label>
                <input
                  name="company_name"
                  value={form.company_name}
                  onChange={onChange}
                  placeholder="Home MDB 2A"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                  data-testid="signup-company"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-1.5">Email *</label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="axel@homemdb2a.fr"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                  data-testid="signup-email"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-1.5">Mot de passe * <span className="text-zinc-600 font-normal">(8 caractères min)</span></label>
                <div className="relative">
                  <input
                    required
                    minLength={8}
                    type={showPwd ? 'text' : 'password'}
                    name="password"
                    value={form.password}
                    onChange={onChange}
                    placeholder="••••••••"
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2.5 pr-10 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                    data-testid="signup-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <label className="flex items-start gap-2 text-xs text-zinc-400 leading-relaxed cursor-pointer pt-2">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-0.5 accent-amber-500"
                  data-testid="signup-accept-cgu"
                />
                <span>
                  J'accepte les <Link to="/cgu" className="text-amber-400 hover:underline">CGU</Link>, les <Link to="/cgv" className="text-amber-400 hover:underline">CGV</Link> et la <Link to="/confidentialite" className="text-amber-400 hover:underline">politique de confidentialité</Link>.
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-950 font-bold transition-all flex items-center justify-center gap-2"
                data-testid="signup-submit"
              >
                {loading ? 'Création du compte...' : (<>Démarrer mon essai 14 jours <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>

            <p className="text-center text-xs text-zinc-500 mt-6">
              En créant un compte, tu acceptes de recevoir des emails liés à ton essai (rappel, fin d'essai, factures).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
